import { inject, Injectable } from '@angular/core';
import { ERROR_CODES } from '../constants/fhir.constants';
import type { ConversionError } from '../interfaces/fhir-converter.interfaces';
import type { FHIRValidationResult } from '../models/fhir.model';
import { FhirAppointmentValidator } from './fhir-appointment.validator';
import { FhirEncounterValidator } from './fhir-encounter.validator';
import { FhirPatientValidator } from './fhir-patient.validator';
import { FhirPractitionerValidator } from './fhir-practitioner.validator';
import { FhirQuestionnaireResponseValidator } from './fhir-questionnaire-response.validator';
import { FhirValidator } from './fhir.validator';

/**
 * FHIR R4 required-field definitions per resource type.
 *
 * Each entry lists the top-level properties that **must** be present according to the R4 specification (cardinality
 * 1..1 or 1..*).
 */
const REQUIRED_FIELDS: Record<string, string[]> = {
  Appointment: ['status', 'participant'],
  CarePlan: ['status', 'intent', 'subject'],
  CareTeam: [],
  Condition: [],
  DiagnosticReport: ['status', 'code'],
  Encounter: ['status', 'class'],
  Immunization: ['status', 'vaccineCode', 'patient', 'occurrenceDateTime'],
  MedicationRequest: ['status', 'intent', 'medicationCodeableConcept', 'subject'],
  Observation: ['status', 'code'],
  Patient: [],
  Practitioner: [],
  Organization: [],
  Procedure: ['status', 'subject'],
  Questionnaire: ['status'],
  QuestionnaireResponse: ['status'],
  RelatedPerson: ['patient'],
  ServiceRequest: ['status', 'intent', 'subject'],
  AllergyIntolerance: [],
  Device: [],
  Location: [],
  Medication: [],
};

/** FHIR R4 valid status value-sets for resources that have a required status field. */
const STATUS_VALUE_SETS: Record<string, string[]> = {
  Appointment: [
    'proposed',
    'pending',
    'booked',
    'arrived',
    'fulfilled',
    'cancelled',
    'noshow',
    'entered-in-error',
    'checked-in',
    'waitlist',
  ],
  CarePlan: ['draft', 'active', 'on-hold', 'revoked', 'completed', 'entered-in-error', 'unknown'],
  DiagnosticReport: [
    'registered',
    'partial',
    'preliminary',
    'final',
    'amended',
    'corrected',
    'appended',
    'cancelled',
    'entered-in-error',
    'unknown',
  ],
  Encounter: [
    'planned',
    'arrived',
    'triaged',
    'in-progress',
    'onleave',
    'finished',
    'cancelled',
    'entered-in-error',
    'unknown',
  ],
  Immunization: ['completed', 'entered-in-error', 'not-done'],
  MedicationRequest: ['active', 'on-hold', 'cancelled', 'completed', 'entered-in-error', 'stopped', 'draft', 'unknown'],
  Observation: [
    'registered',
    'preliminary',
    'final',
    'amended',
    'corrected',
    'cancelled',
    'entered-in-error',
    'unknown',
  ],
  Procedure: [
    'preparation',
    'in-progress',
    'not-done',
    'on-hold',
    'stopped',
    'completed',
    'entered-in-error',
    'unknown',
  ],
  Questionnaire: ['draft', 'active', 'retired', 'unknown'],
  QuestionnaireResponse: ['in-progress', 'completed', 'amended', 'entered-in-error', 'stopped'],
  ServiceRequest: ['draft', 'active', 'on-hold', 'revoked', 'completed', 'entered-in-error', 'unknown'],
};

/** Supported FHIR resource type names. */
const VALID_RESOURCE_TYPES = Object.keys(REQUIRED_FIELDS);

/**
 * General-purpose FHIR R4 resource validator.
 *
 * Validates **any** FHIR resource for:
 *
 * - Presence and validity of `resourceType`
 * - Required fields per R4 specification
 * - Status value-set conformance
 * - Delegates Questionnaire-specific deep validation to {@link FhirValidator}
 *
 * Returns the standard {@link FHIRValidationResult} from conductor.
 */
@Injectable({ providedIn: 'root' })
export class FhirResourceValidator {
  private readonly questionnaireValidator = inject(FhirValidator);
  private readonly questionnaireResponseValidator = inject(FhirQuestionnaireResponseValidator);
  private readonly patientValidator = inject(FhirPatientValidator);
  private readonly practitionerValidator = inject(FhirPractitionerValidator);
  private readonly encounterValidator = inject(FhirEncounterValidator);
  private readonly appointmentValidator = inject(FhirAppointmentValidator);

  /**
   * Validate a FHIR resource object.
   *
   * @param resource - The parsed resource object (any type).
   * @returns A {@link FHIRValidationResult} with errors and warnings.
   */
  validate(resource: unknown): FHIRValidationResult {
    const errors: ConversionError[] = [];
    const warnings: string[] = [];

    if (!resource || typeof resource !== 'object') {
      errors.push({
        code: ERROR_CODES.INVALID_INPUT,
        message: 'Resource must be a non-null object',
        severity: 'error',
      });
      return this.toResult(errors, warnings);
    }

    const r = resource as Record<string, unknown>;

    // 1. resourceType must exist
    if (!r['resourceType']) {
      errors.push({
        code: ERROR_CODES.INVALID_RESOURCE_TYPE,
        message: 'Missing required field: resourceType',
        path: 'resourceType',
        severity: 'error',
      });
      return this.toResult(errors, warnings);
    }

    const resourceType = r['resourceType'] as string;

    // 2. resourceType must be recognised
    if (!VALID_RESOURCE_TYPES.includes(resourceType)) {
      errors.push({
        code: ERROR_CODES.INVALID_RESOURCE_TYPE,
        message: `Unknown resourceType "${resourceType}". Expected one of: ${VALID_RESOURCE_TYPES.join(', ')}`,
        path: 'resourceType',
        severity: 'error',
      });
      return this.toResult(errors, warnings);
    }

    // 3. Required fields
    const requiredFields = REQUIRED_FIELDS[resourceType] ?? [];
    for (const field of requiredFields) {
      const value = r[field];
      if (value === undefined || value === null) {
        errors.push({
          code: 'MISSING_REQUIRED_FIELD',
          message: `Missing required field: ${field}`,
          path: field,
          severity: 'error',
        });
      } else if (Array.isArray(value) && value.length === 0) {
        errors.push({
          code: 'MISSING_REQUIRED_FIELD',
          message: `Required field "${field}" must have at least one entry`,
          path: field,
          severity: 'error',
        });
      }
    }

    // 4. Status value-set enforcement
    const statusValues = STATUS_VALUE_SETS[resourceType];
    if (statusValues && r['status']) {
      const status = r['status'] as string;
      if (!statusValues.includes(status)) {
        errors.push({
          code: ERROR_CODES.INVALID_STATUS,
          message: `Invalid status "${status}" for ${resourceType}. Must be one of: ${statusValues.join(', ')}`,
          path: 'status',
          severity: 'error',
        });
      }
    }

    // 5. Resource-specific deep validation
    switch (resourceType) {
      case 'Questionnaire':
        this.questionnaireValidator.validate(resource, errors, warnings);
        break;
      case 'QuestionnaireResponse':
        this.questionnaireResponseValidator.validate(resource, errors, warnings);
        break;
      case 'Patient':
        this.patientValidator.validate(resource, errors, warnings);
        break;
      case 'Practitioner':
        this.practitionerValidator.validate(resource, errors, warnings);
        break;
      case 'Encounter':
        this.encounterValidator.validate(resource, errors, warnings);
        break;
      case 'Appointment':
        this.appointmentValidator.validate(resource, errors, warnings);
        break;
    }

    // 6. Informational warnings
    if (!r['id']) {
      warnings.push(`Resource does not have an id — server will assign one on create`);
    }

    return this.toResult(errors, warnings);
  }

  /** Parse a FHIR OperationOutcome (returned by servers on error) into a {@link FHIRValidationResult}. */
  parseOperationOutcome(outcome: unknown): FHIRValidationResult {
    const errors: ConversionError[] = [];
    const warnings: string[] = [];

    if (!outcome || typeof outcome !== 'object') {
      return this.toResult(errors, warnings);
    }

    const o = outcome as Record<string, unknown>;
    if (o['resourceType'] !== 'OperationOutcome' || !Array.isArray(o['issue'])) {
      return this.toResult(errors, warnings);
    }

    for (const issue of o['issue'] as Record<string, unknown>[]) {
      const severity = issue['severity'] as string;
      const message =
        (issue['diagnostics'] as string) ||
        ((issue['details'] as Record<string, unknown>)?.['text'] as string) ||
        'Unknown error';
      const path = (issue['expression'] as string[])?.[0] || (issue['location'] as string[])?.[0] || undefined;
      const code = (issue['code'] as string) || 'SERVER_ERROR';

      if (severity === 'error' || severity === 'fatal') {
        errors.push({ code, message, path, severity: 'error' });
      } else {
        warnings.push(`${path ? path + ': ' : ''}${message}`);
      }
    }

    return this.toResult(errors, warnings);
  }

  /** Check whether a string is a supported FHIR resource type. */
  isSupportedResourceType(value: string): boolean {
    return VALID_RESOURCE_TYPES.includes(value);
  }

  /** List all supported FHIR resource types. */
  getSupportedResourceTypes(): readonly string[] {
    return VALID_RESOURCE_TYPES;
  }

  /** Get required fields for a resource type. */
  getRequiredFields(resourceType: string): readonly string[] {
    return REQUIRED_FIELDS[resourceType] ?? [];
  }

  // ------------------------------------------------------------------
  // Private helpers
  // ------------------------------------------------------------------

  private toResult(errors: ConversionError[], warnings: string[]): FHIRValidationResult {
    return {
      valid: errors.filter((e) => e.severity === 'error').length === 0,
      errors: errors
        .filter((e) => e.severity === 'error')
        .map((e) => ({ severity: 'error' as const, location: e.path ?? '', message: e.message, code: e.code })),
      warnings: warnings.map((w) => ({ severity: 'warning' as const, location: '', message: w })),
    };
  }
}
